import _ from "lodash";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import classNames from "classnames";

import { Popover } from "@toddle-edu/ds-web";

import { graphql, compose } from "lib/apolloClient";
import { useDebouncedCallback, useHandleScroll } from "Utils/CustomHooks";
import {
  PLANNER,
  STUDENT_ALERTS_BODY_WRAPPER_ID,
} from "Constants/stringConstants";

import SkeletonLoader from "UIComponents/SkeletonLoader";
import {
  getStudentAlertTypeIcon,
  getIsStudentAlertsEnabled,
} from "StudentAlertsSettings/modules/StudentAlertsSettingsUtils";

import {
  getStudentAlertTypesQuery,
  getStudentAlertsQuery,
} from "./modules/Queries";
import { AlertCard } from "./components";
import classes from "./StudentAlerts.scss";

const styles = {
  popoverContainerStyle: {
    zIndex: 3000,
    maxWidth: "360px",
  },
};

const AlertLoader = () => {
  return (
    <div className={classes.loaderContainer}>
      <div className={classes.loaderHeader}>
        <SkeletonLoader variant="circular" height="20px" width="20px" />
        <SkeletonLoader variant="rounded" height="20px" />
      </div>
      <SkeletonLoader variant="rounded" height="60px" />
    </div>
  );
};

const AlertBody = props => {
  const {
    alerts,
    hasNextPage,
    loadMore,
    isData,
    isLoading,
    isLoadingMore,
    networkStatus,
    closePopover,
  } = props;
  const alertsContainerRef = useRef(null);

  useEffect(() => {
    const handleScroll = ({ target }) => {
      if (!alertsContainerRef.current?.contains(target)) {
        closePopover();
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMore ?? _.noop,
    hasNextPage,
    getScrollRef: () => alertsContainerRef.current,
  });

  const handleEventPropagation = event => {
    event?.stopPropagation();
  };

  if (!isData && isLoading) {
    return <AlertLoader />;
  }

  return (
    <div
      className={classes.alertBodyContainer}
      onClick={handleEventPropagation}
      id={STUDENT_ALERTS_BODY_WRAPPER_ID}
    >
      <div
        tabIndex={0}
        className={classes.alertsContainer}
        ref={alertsContainerRef}
      >
        {_.map(alerts, (alert, index) => {
          const { id } = alert;
          const showDivider = index !== _.size(alerts) - 1;

          return (
            <div className={classes.alertCardContainer} key={id}>
              <AlertCard alert={alert} />
              {showDivider ? <div className={classes.divider} /> : null}
            </div>
          );
        })}
        {isLoadingMore && <AlertLoader />}
      </div>
    </div>
  );
};

const alertBodyMapStateToProps = () => {
  return {
    isData: true,
    isLoading: false,
    isMiniLoader: true,
  };
};

const AlertBodyWithData = compose(
  connect(alertBodyMapStateToProps),
  graphql(getStudentAlertsQuery, {
    name: "getStudentAlerts",
    options: ({ studentId, alertType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: studentId,
        first: 10,
        filters: {
          status: "ACTIVE",
          studentAlertTypeIds: [alertType.id],
        },
        portalType: PLANNER,
      },
    }),
    props({
      getStudentAlerts: { networkStatus, node, fetchMore, variables },
      ownProps: { isData, isLoading },
    }) {
      const edges = _.get(node, "alerts.edges", []);
      const alerts = _.map(edges, "node");
      const { hasNextPage, endCursor } = _.get(node, "alerts.pageInfo", {});

      return {
        alerts,
        isData: !_.isEmpty(node) && isData,
        isLoading: isLoading || _.includes([1, 2], networkStatus),
        loadMore: () => {
          fetchMore({
            variables: {
              ...variables,
              after: endCursor,
            },
          });
        },
        hasNextPage,
        networkStatus,
        isLoadingMore: networkStatus === 3,
      };
    },
  })
)(AlertBody);

const Alerts = props => {
  const {
    alertTypes,
    isData,
    isLoading,
    studentId,
    showAlertPopover = true,
    isAlertsEnabled,
    iconProps = {},
    shouldAutoFocusPopover,
  } = props;

  const [popoverConfig, setPopoverConfig] = useState({
    isVisible: false,
    alertId: null,
  });

  const openPopover = useCallback(
    alertId => {
      if (!showAlertPopover) {
        return;
      }
      setPopoverConfig({ isVisible: true, alertId });
    },
    [showAlertPopover]
  );

  const [debounceOpenPopover] = useDebouncedCallback(openPopover, 300, [
    openPopover,
  ]);

  const closePopover = useCallback(() => {
    debounceOpenPopover?.cancel();
    setPopoverConfig({ isVisible: false, alertId: null });
  }, [debounceOpenPopover]);

  if (!isAlertsEnabled) {
    return null;
  }

  if (!isData && isLoading) {
    return <SkeletonLoader variant="rounded" height="20px" width="100px" />;
  }

  if (isData && _.isEmpty(alertTypes)) {
    return null;
  }

  const iconContainerClasses = classNames(classes.iconContainer, {
    [classes.cursorPointer]: showAlertPopover,
  });

  return (
    <div className={classes.container}>
      {_.map(alertTypes, alertType => {
        const { id, icon } = alertType;
        const Icon = getStudentAlertTypeIcon({ iconMetaData: icon });

        const openAlertPopover = () => {
          debounceOpenPopover(id);
        };

        return (
          <div onMouseEnter={openAlertPopover} onMouseLeave={closePopover}>
            <Popover
              key={id}
              isOpen={popoverConfig.isVisible && popoverConfig.alertId === id}
              onClickOutside={closePopover}
              body={
                <AlertBodyWithData
                  alertType={alertType}
                  studentId={studentId}
                  closePopover={closePopover}
                />
              }
              dsVersion="2.0"
              isClosable={false}
              placement="bottom"
              containerStyle={styles.popoverContainerStyle}
              align="start"
              shouldAutoFocus={shouldAutoFocusPopover}
            >
              <div className={iconContainerClasses}>
                <Icon size="xxx-small" {...iconProps} />
              </div>
            </Popover>
          </div>
        );
      })}
    </div>
  );
};

const mapStateToProps = () => {
  const isAlertsEnabled = getIsStudentAlertsEnabled();

  return {
    isData: true,
    isLoading: false,
    isAlertsEnabled,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getStudentAlertTypesQuery, {
    name: "getStudentAlertTypes",
    skip: ({
      studentId,
      alertTypes,
      isAlertsEnabled,
      shouldFetchAlertTypes = false,
    }) => {
      if (!studentId || !isAlertsEnabled) {
        return true;
      }

      return !_.isNil(alertTypes) && !shouldFetchAlertTypes;
    },
    options: ({ studentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: studentId,
      },
    }),
    props({
      getStudentAlertTypes: { networkStatus, node },
      ownProps: { isData, isLoading },
    }) {
      const alertTypes = _.get(node, "alertTypes", []);

      return {
        alertTypes,
        isData: !_.isEmpty(node) && isData,
        isLoading: isLoading || _.includes([1, 2], networkStatus),
      };
    },
  })
)(Alerts);
